import { useMemo } from 'react';

import { paths } from 'src/routes/paths';

import { useHasPermission } from 'src/hooks/use-has-permission';

import { useGetStoreModulePermissionsQuery } from 'src/graphql/generated';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import SvgColor from 'src/components/svg-color';

import { groupNavigation } from './group-navigation';
import { AUTOMATION_AUTHORING_MODE, getAutomationAuthoringMode } from './automation-navigation';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  user: icon('ic_user'),
  customer: icon('ic_customer'),
  file: icon('ic_file'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  coupon: <Iconify icon="solar:ticket-sale-bold-duotone" width={24} />,
  loyalty: <Iconify icon="solar:gift-bold-duotone" width={24} />,
  product: icon('ic_hanger'),
  totem: icon('ic_totem'),
  analytics: icon('ic_analytics'),
  template_campaign: icon('ic_blog'),
  mass_campaign: icon('ic_voice'),
  journeys: <Iconify icon="solar:branching-paths-up-bold-duotone" sx={{ width: 1, height: 1 }} />,
  settings: icon('ic_settings'),
  service: icon('ic_invoice'),
  appointment: icon('ic_calendar'),
};

const NEW_BADGE = (
  <Label color="info" variant="filled" sx={{ height: 18, fontSize: 10 }}>
    Novo
  </Label>
);

// ----------------------------------------------------------------------

export function useNavData() {
  const { hasAdminPermission, hasSellerPermissions, isLoading } = useHasPermission();
  const { data: modulePermissionsData, loading: modulePermissionsLoading } =
    useGetStoreModulePermissionsQuery();
  const automationAuthoringMode = getAutomationAuthoringMode(
    modulePermissionsData?.getStoreModulePermissions?.hasJourneys
  );

  const data = useMemo(
    () => [
      // OVERVIEW
      // ----------------------------------------------------------------------
      {
        subheader: 'Zeki',
        items: [
          {
            title: 'Home',
            path: paths.dashboard.root,
            icon: ICONS.tour,
          },
          ...(hasSellerPermissions
            ? [
                {
                  title: 'Clientes',
                  path: paths.dashboard.customer.root,
                  icon: ICONS.customer,
                },
                {
                  title: 'PDV',
                  path: paths.dashboard.pdv.root,
                  icon: ICONS.file,
                },
                {
                  title: 'Giftbacks',
                  path: paths.dashboard.giftback.root,
                  icon: ICONS.label,
                },
                {
                  title: 'Vendas',
                  path: paths.dashboard.purchase.root,
                  icon: ICONS.order,
                },
                {
                  title: 'Produtos',
                  path: paths.dashboard.product.root,
                  icon: ICONS.product,
                  hasPermissionsPremium:
                    modulePermissionsData?.getStoreModulePermissions?.hasProducts,
                },
                {
                  title: 'Serviços',
                  path: paths.dashboard.service.root,
                  icon: ICONS.service,
                  hasPermissionsPremium:
                    modulePermissionsData?.getStoreModulePermissions?.hasServices,
                },
                {
                  title: 'Agenda',
                  path: paths.dashboard.appointment.root,
                  icon: ICONS.appointment,
                  hasPermissionsPremium:
                    modulePermissionsData?.getStoreModulePermissions?.hasAppointments &&
                    modulePermissionsData?.getStoreModulePermissions?.hasServices,
                },
                {
                  title: 'Totem',
                  path: paths.totem,
                  icon: ICONS.totem,
                },
              ]
            : [
                ...(hasAdminPermission
                  ? [
                      {
                        title: 'Usuários',
                        path: paths.dashboard.user.root,
                        icon: ICONS.user,
                      },
                    ]
                  : []),
                {
                  title: 'Dashboards',
                  path: paths.dashboard.analytics.customer,
                  icon: ICONS.analytics,
                  hasPermissionsPremium:
                    modulePermissionsData?.getStoreModulePermissions?.hasDashboards,
                  hasNew: true,
                  info: NEW_BADGE,
                  children: [
                    {
                      title: 'Clientes',
                      path: paths.dashboard.analytics.customer,
                      hasPermissionsPremium:
                        modulePermissionsData?.getStoreModulePermissions?.hasDashboards,
                    },
                    {
                      title: 'Matriz RFM',
                      path: paths.dashboard.analytics.rfm,
                      hasPermissionsPremium:
                        modulePermissionsData?.getStoreModulePermissions?.hasDashboards,
                    },
                    {
                      title: 'Retenção',
                      path: paths.dashboard.analytics.retention,
                      hasPermissionsPremium:
                        modulePermissionsData?.getStoreModulePermissions?.hasDashboards,
                      info: NEW_BADGE,
                    },
                    {
                      title: 'Giftbacks',
                      path: paths.dashboard.analytics.giftback,
                      hasPermissionsPremium:
                        modulePermissionsData?.getStoreModulePermissions?.hasDashboards,
                    },
                    {
                      title: 'Vendas',
                      path: paths.dashboard.analytics.sales,
                      hasPermissionsPremium:
                        modulePermissionsData?.getStoreModulePermissions?.hasDashboards,
                    },
                  ],
                },
                {
                  title: 'Clientes',
                  path: paths.dashboard.customer.root,
                  icon: ICONS.customer,
                },
                {
                  title: 'Giftbacks',
                  path: paths.dashboard.giftback.root,
                  icon: ICONS.label,
                },
                ...(hasAdminPermission
                  ? [
                      {
                        title: 'Fidelidade',
                        path: paths.dashboard.loyalty.root,
                        icon: ICONS.loyalty,
                        hasPermissionsPremium:
                          modulePermissionsData?.getStoreModulePermissions?.hasLoyalty,
                      },
                    ]
                  : []),
                {
                  title: 'PDV',
                  path: paths.dashboard.pdv.root,
                  icon: ICONS.file,
                  hasPermissionsPremium: modulePermissionsData?.getStoreModulePermissions?.hasPdv,
                },
                {
                  title: 'Vendas',
                  path: paths.dashboard.purchase.root,
                  icon: ICONS.order,
                },
                {
                  title: 'Produtos',
                  path: paths.dashboard.product.root,
                  icon: ICONS.product,
                  hasPermissionsPremium:
                    modulePermissionsData?.getStoreModulePermissions?.hasProducts,
                },
                {
                  title: 'Serviços',
                  path: paths.dashboard.service.root,
                  icon: ICONS.service,
                  hasPermissionsPremium:
                    modulePermissionsData?.getStoreModulePermissions?.hasServices,
                },
                {
                  title: 'Agenda',
                  path: paths.dashboard.appointment.root,
                  icon: ICONS.appointment,
                  hasPermissionsPremium:
                    modulePermissionsData?.getStoreModulePermissions?.hasAppointments &&
                    modulePermissionsData?.getStoreModulePermissions?.hasServices,
                  children: [
                    { title: 'Visão operacional', path: paths.dashboard.appointment.root },
                    { title: 'Dashboard', path: paths.dashboard.appointment.dashboard },
                    ...(hasAdminPermission
                      ? [
                          {
                            title: 'Configurações',
                            path: paths.dashboard.appointment.settings,
                          },
                        ]
                      : []),
                  ],
                },
                {
                  title: 'Totem',
                  path: paths.totem,
                  icon: ICONS.totem,
                },
                ...(automationAuthoringMode === AUTOMATION_AUTHORING_MODE.JOURNEYS
                  ? [
                      {
                        title: 'Jornadas',
                        path: paths.dashboard.jornadas.root,
                        icon: ICONS.journeys,
                      },
                    ]
                  : [
                      {
                        title: 'Automações',
                        path: paths.dashboard.message.root,
                        icon: ICONS.template_campaign,
                      },
                    ]),
                {
                  title: 'Planejamento',
                  path: paths.dashboard.planning,
                  icon: <Iconify icon="solar:calendar-bold-duotone" width={24} />,
                },
                {
                  title: 'Campanhas',
                  path: paths.dashboard.campaign.root,
                  icon: ICONS.mass_campaign,
                  hasPermissionsPremium:
                    modulePermissionsData?.getStoreModulePermissions?.hasBulkNotification,
                },
                {
                  title: 'Comunidades',
                  path: paths.dashboard.communities.root,
                  icon: <Iconify icon="solar:users-group-rounded-bold-duotone" width={24} />,
                },
                {
                  title: 'Cupons',
                  path: paths.dashboard.coupon.root,
                  icon: ICONS.coupon,
                  hasPermissionsPremium:
                    modulePermissionsData?.getStoreModulePermissions?.hasCoupons,
                },
                ...(hasAdminPermission
                  ? [
                      {
                        title: 'Configurações',
                        path: paths.dashboard.config.root,
                        icon: ICONS.settings,
                      },
                    ]
                  : []),
              ]),
        ],
      },
    ],
    [automationAuthoringMode, hasAdminPermission, hasSellerPermissions, modulePermissionsData]
  );

  return !isLoading && !modulePermissionsLoading ? groupNavigation(data[0].items) : [];
}
